import * as v from 'valibot';

import { vcGcid, vcTwoLetterCountryCode } from '../../../../utils/valibot/custom-types';
import { newSchema } from '../../../../utils/valibot/schema';

export enum PolicyDocumentUsageMimeType {
  PLAIN_TEXT = 'text/plain',
  HTML = 'text/html',
}

export enum PolicyDocumentUsageTag {
  HEADER = 'header',
  AFTER_USAGE_DEF = 'afterUsageDef',
  BEFORE_USAGE_DEF = 'beforeUsageDef',
  POPUP = 'popup',
}

export enum PolicyDocumentVariant {
  IMPLICIT = 'IMPLICIT',
  OPTIONAL = 'OPTIONAL',
  MANDATORY = 'MANDATORY',
}

export const PolicyDocumentUsageSchema = newSchema(
  'PolicyDocumentUsageSchema',
  v.object({
    id: v.number(),
    name: v.string(),
    lockable: v.optional(v.boolean()),
    implicit: v.optional(v.boolean()),
    mandatory: v.optional(v.boolean()),
    optional: v.optional(v.boolean()),
    visible: v.optional(v.boolean()),
    label: v.optional(v.string()),
    allowedVariants: v.optional(v.array(v.enum(PolicyDocumentVariant))),
    description: v.optional(v.string()),
    semantic: v.optional(v.string()),
  }),
);

export const PolicyDocumentUsageItemSchema = newSchema(
  'PolicyDocumentUsageItemSchema',
  v.object({
    id: v.number(),
    variant: v.enum(PolicyDocumentVariant),
    ordinalNumber: v.number(),
    usage: PolicyDocumentUsageSchema,
    formerUsageId: v.optional(v.string()),
  }),
);

export const PolicyDocumentLegalEntitySchema = newSchema(
  'PolicyDocumentLegalEntitySchema',
  v.object({
    id: v.number(),
    name: v.string(),
    label: v.string(),
    country: v.string(),
    visible: v.boolean(),
  }),
);

export const PolicyDocumentPurposeSchema = newSchema(
  'PolicyDocumentPurposeSchema',
  v.object({
    id: v.number(),
    name: v.string(),
    visible: v.boolean(),
  }),
);

export const PolicyDocumentPurposeItemSchema = newSchema(
  'PolicyDocumentPurposeItemSchema',
  v.object({
    id: v.number(),
    validityInDays: v.number(),
    purpose: PolicyDocumentPurposeSchema,
  }),
);

export const PolicyDocumentUsageDefinitionSchema = newSchema(
  'PolicyDocumentUsageDefinitionSchema',
  v.object({
    id: v.number(),
    textPrimary: v.optional(v.string()),
    mimeTypeTextPrimary: v.optional(v.string()),
    textSecondary: v.optional(v.string()),
    mimeTypeTextSecondary: v.optional(v.string()),
    ordinalNumber: v.number(),
    usages: v.array(PolicyDocumentUsageItemSchema),
    legalEntities: v.array(PolicyDocumentLegalEntitySchema),
    purposes: v.array(PolicyDocumentPurposeItemSchema),
  }),
);

export const PolicyDocumentUsageTextSchema = newSchema(
  'PolicyDocumentUsageTextSchema',
  v.object({
    id: v.number(),
    value: v.string(),
    mimeType: v.enum(PolicyDocumentUsageMimeType),
    textBlockId: v.number(),
    ordinalNumber: v.number(),
    tag: v.enum(PolicyDocumentUsageTag),
  }),
);

export const PolicyDocumentTextBlockSchema = newSchema(
  'PolicyDocumentTextBlockSchema',
  v.object({
    id: v.number(),
    language: v.string(),
    usageDefinitions: v.optional(v.array(PolicyDocumentUsageDefinitionSchema)),
    usageText: v.optional(v.array(PolicyDocumentUsageTextSchema)),
  }),
);

export const PolicyDocumentContactFrequencySchema = newSchema(
  'PolicyDocumentContactFrequencySchema',
  v.object({
    frequencyDays: v.number(),
  }),
);

export const PolicyDocumentSchema = newSchema(
  'PolicyDocumentSchema',
  v.object({
    id: v.number(),
    brand: v.optional(v.string()),
    name: v.string(),
    validFrom: v.string(),
    country: vcTwoLetterCountryCode,
    majorVersion: v.number(),
    minorVersion: v.number(),
    description: v.string(),
    textBlocks: v.array(PolicyDocumentTextBlockSchema),
    contactFrequency: PolicyDocumentContactFrequencySchema,
    touchpointLinks: v.array(v.unknown()),
    encodedPolicyDocuments: v.array(v.unknown()),
  }),
);

export enum GcdmConsentState {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
}

const internalGcdmConsentSchema = v.object({
  consent: v.enum(GcdmConsentState),
  policyId: v.string(),
  country: vcTwoLetterCountryCode,
  majorVersion: v.number(),
  minorVersion: v.number(),
  usageId: v.string(),
  purposeId: v.string(),
  touchpointId: v.string(),
  language: v.optional(v.string()),
  consentDate: v.string(),
  legalEntityId: v.string(),
  expirationDate: v.optional(v.string()),
  consentGroupId: v.string(),
  brand: v.optional(v.string()),
});

export const GcdmConsentSchema = newSchema('GcdmConsentSchema', internalGcdmConsentSchema);

export const GcdmConsentsSchema = newSchema('GcdmConsentsSchema', v.array(GcdmConsentSchema));

export const GcdmConsentWithDateSchema = newSchema(
  'GcdmConsentWithDateSchema',
  v.object({
    ...internalGcdmConsentSchema.entries,
    parsedConsentDate: v.pipe(v.date()),
  }),
);

export type PolicyDocumentUsage = v.InferOutput<typeof PolicyDocumentUsageSchema>;
export type PolicyDocumentUsageItem = v.InferOutput<typeof PolicyDocumentUsageItemSchema>;
export type PolicyDocumentLegalEntity = v.InferOutput<typeof PolicyDocumentLegalEntitySchema>;
export type PolicyDocumentPurpose = v.InferOutput<typeof PolicyDocumentPurposeSchema>;
export type PolicyDocumentPurposeItem = v.InferOutput<typeof PolicyDocumentPurposeItemSchema>;
export type PolicyDocumentUsageDefinition = v.InferOutput<typeof PolicyDocumentUsageDefinitionSchema>;
export type PolicyDocumentUsageText = v.InferOutput<typeof PolicyDocumentUsageTextSchema>;
export type PolicyDocumentTextBlock = v.InferOutput<typeof PolicyDocumentTextBlockSchema>;
export type PolicyDocumentContactFrequency = v.InferOutput<typeof PolicyDocumentContactFrequencySchema>;
export type PolicyDocument = v.InferOutput<typeof PolicyDocumentSchema>;
export type GcdmConsent = v.InferOutput<typeof GcdmConsentSchema>;
export type GcdmConsents = v.InferOutput<typeof GcdmConsentsSchema>;
export type GcdmConsentWithDate = v.InferOutput<typeof GcdmConsentWithDateSchema>; // Extends GcdmConsent => works by Duck Typing

export const PreregistrationSuccessSchema = newSchema(
  'PreregistrationSuccessSchema',
  v.object({
    emailAddress: v.pipe(v.string(), v.email()),
    gcid: vcGcid,
    token: v.string(),
    expiresAt: v.pipe(v.string(), v.isoTimestamp()),
  }),
);

export const PreregistrationResponseSchema = newSchema(
  'PreregistrationResponseSchema',
  v.union([
    v.object({ successes: v.array(PreregistrationSuccessSchema), errors: v.optional(v.undefined()) }),
    v.object({ errors: v.array(v.looseObject({})), successes: v.optional(v.undefined()) }),
  ]),
);

export type PreregistrationSuccess = v.InferOutput<typeof PreregistrationSuccessSchema>;
export type PreregistrationResponse = v.InferOutput<typeof PreregistrationResponseSchema>;
